// @ts-nocheck
import React, { Component } from "react";
import { toast } from "react-toastify";
import ApiService from "../../services/api.service";
import { apiUrlConfig } from "../../constants/apiUrlConfig";
import "./postman-uploader.css";

class PostmanEnvironmentUploader extends Component {
  constructor(props) {
    super(props);
    this.state = {
      environment: null,
      variables: [],
      error: null,
      selectedVariables: {},
      selectedAll: true,
      nameErrors: {},
      valueErrors: {},
      environmentName: "",
      isGlobal: false,
      fileName: "",
      // Configuration-file mode. A plain config (program_configuration
      // .json and the like) is not a Postman environment, so it is
      // parsed on the SERVER: credential classification and encryption
      // must not depend on anything a browser decides.
      configMode: false,
      configFile: null,
      configEnvironments: [],
      selectedEnvironments: {},
      configLoading: false
    };
    this.apiService = ApiService.getInstance();
  }

  handleFileChange = event => {
    const file = event.target.files[0];

    if (file) {
      // The File itself is kept: a configuration is re-sent to the
      // import endpoint, and re-reading it in the browser would mean
      // holding its credentials in component state.
      this.setState({
        fileName: file.name,
        configFile: file,
        configMode: false,
        configEnvironments: [],
        selectedEnvironments: {},
        environment: null,
        variables: []
      });
      const reader = new FileReader();
      reader.onload = this.handleFileLoad;
      reader.readAsText(file);
    }
  };

  handleFileLoad = event => {
    const fileContent = event.target.result;

    try {
      const environmentObject = JSON.parse(fileContent);

      if (this.isValidPostmanEnvironment(environmentObject)) {
        const variables = this.parseVariables(environmentObject.values);

        this.setState(
          {
            environment: environmentObject,
            variables,
            error: null,
            selectedVariables: this.initializeSelectedVariables(variables),
            environmentName: environmentObject.name || ""
          },
          () => {
            this.validateUniqueNames();
          }
        );
      } else {
        // Not a Postman environment -- try it as a configuration file.
        this.previewConfig();
      }
    } catch (error) {
      toast.error("Something went wrong while parsing the file. Please try again!");
    }
  };

  isValidPostmanEnvironment = environment => {
    return (
      environment &&
      environment.values &&
      Array.isArray(environment.values) &&
      environment.name &&
      environment._postman_variable_scope === "environment"
    );
  };

  // Convert Postman's {{variable}} syntax to our system's {variable} syntax
  convertPostmanVariables = (text) => {
    if (!text) return "";
    // Replace {{variable}} with {variable} - our system uses single curly braces
    return String(text).replace(/{{(.*?)}}/g, (_, key) => `{${key}}`);
  };

  parseVariables = variableArray => {
    if (variableArray) {
      return variableArray.map(variable => ({
        name: variable.key || "",
        // Convert any {{variable}} references in values to {variable}
        value: this.convertPostmanVariables(variable.value || ""),
        type: "String",
        description: "postman environment variable"
      }));
    }
    return [];
  };

  /** unsuccess(msg) lands in `message`; older endpoints use `error`. */
  messageOf = (res, fallback) =>
    (res && res.message) || (res && res.error && res.error.description) || fallback;

  /**
   * Ask the server what this configuration would create.
   *
   * Parsing on the server is deliberate. Which keys count as
   * credentials, and the encryption that follows, decide whether a
   * secret is ever written in the clear -- that must not be a decision
   * the browser makes or can be talked out of.
   */
  previewConfig = async () => {
    const { configFile } = this.state;
    const groupId = this.props.groupId;
    if (!configFile) return;
    if (!groupId) {
      toast.error("Cannot import a configuration here: no group selected.");
      return;
    }

    this.setState({ configLoading: true });
    try {
      const form = new FormData();
      form.append("file", configFile);
      const res = await this.apiService.postFile(
        apiUrlConfig.config_import_preview(groupId),
        form
      );
      if (!res || !res.success) {
        this.setState({ configLoading: false });
        toast.error(
          this.messageOf(res, "That file is neither a Postman environment nor a readable configuration.")
        );
        return;
      }
      const environments = (res.payload && res.payload.environments) || [];
      const selected = {};
      environments.forEach(env => {
        selected[env.name] = true;
      });
      this.setState({
        configMode: true,
        configEnvironments: environments,
        selectedEnvironments: selected,
        configLoading: false,
        error: null
      });
    } catch (e) {
      this.setState({ configLoading: false });
      toast.error("Could not read the configuration file.");
    }
  };

  toggleEnvironment = name => {
    this.setState(prev => ({
      selectedEnvironments: {
        ...prev.selectedEnvironments,
        [name]: !prev.selectedEnvironments[name]
      }
    }));
  };

  selectedEnvironmentNames = () => {
    const { selectedEnvironments } = this.state;
    return Object.keys(selectedEnvironments).filter(k => selectedEnvironments[k]);
  };

  /** Create one variable set per selected environment. */
  importConfig = async () => {
    const { configFile, isGlobal } = this.state;
    const groupId = this.props.groupId;
    const names = this.selectedEnvironmentNames();
    if (!configFile || names.length === 0) return;

    this.setState({ configLoading: true });
    try {
      const form = new FormData();
      form.append("file", configFile);
      names.forEach(n => form.append("environments", n));
      if (isGlobal) form.append("isGlobal", "true");

      const res = await this.apiService.postFile(
        apiUrlConfig.config_import(groupId),
        form
      );
      if (!res || !res.success) {
        this.setState({ configLoading: false });
        toast.error(this.messageOf(res, "Could not import the configuration."));
        return;
      }

      const created = (res.payload && res.payload.created) || [];
      const skipped = (res.payload && res.payload.skipped) || [];
      created.forEach(c =>
        toast.success(
          `Created ${c.name}: ${c.variables} variables` +
            (c.secrets > 0 ? `, ${c.secrets} encrypted` : "")
        )
      );
      // Re-importing the same file skips the sets that already exist.
      // Not a failure, but it has to be visible or the count disagrees
      // with what was ticked.
      skipped.forEach(sk => toast.info(`Skipped: ${sk}`));
      if (created.length === 0 && skipped.length === 0) {
        toast.info("Nothing was created.");
      }

      this.setState({ configLoading: false });
      if (this.props.onConfigImported) this.props.onConfigImported(created);
      this.props.close();
    } catch (e) {
      this.setState({ configLoading: false });
      toast.error("Could not import the configuration.");
    }
  };

  initializeSelectedVariables = (variables, defaultValue = true) => {
    const selectedVariables = {};
    variables.forEach((_variable, index) => {
      selectedVariables[index] = defaultValue;
    });
    return selectedVariables;
  };

  handleCheckboxChange = index => {
    this.setState(prevState => ({
      selectedVariables: {
        ...prevState.selectedVariables,
        [index]: !prevState.selectedVariables[index]
      }
    }));
  };

  handleSelectAll = () => {
    this.setState(prevState => {
      const { selectedAll, selectedVariables } = prevState;

      const updatedSelectedVariables = Object.keys(selectedVariables).reduce((acc, key) => {
        acc[key] = !selectedAll;
        return acc;
      }, {});

      return {
        selectedVariables: updatedSelectedVariables,
        selectedAll: !selectedAll
      };
    });
  };

  handleVariableChange = (index, field, newValue) => {
    this.setState(
      prevState => {
        const updatedVariables = [...prevState.variables];
        updatedVariables[index][field] = newValue;
        return { variables: updatedVariables };
      },
      () => {
        if (field === "name") {
          this.validateUniqueNames();
        }
        if (field === "value" || field === "type") {
          this.validateVariableValue(index);
        }
      }
    );
  };

  validateUniqueNames = () => {
    const { variables } = this.state;
    const nameCounts = variables.reduce((acc, variable) => {
      const name = variable.name.trim();
      if (name) {
        acc[name] = (acc[name] || 0) + 1;
      }
      return acc;
    }, {});

    const nameErrors = {};
    variables.forEach((variable, index) => {
      const name = variable.name.trim();
      if (name && nameCounts[name] > 1) {
        nameErrors[index] = "Name must be unique.";
      } else if (!name) {
        nameErrors[index] = "Name is required.";
      } else {
        nameErrors[index] = "";
      }
    });

    this.setState({ nameErrors });
  };

  validateVariableValue = index => {
    const { variables } = this.state;
    const variable = variables[index];
    let errorMessage = "";

    if (variable.type === "Boolean") {
      const value = variable.value.trim().toLowerCase();
      if (value !== "true" && value !== "false") {
        errorMessage = 'Value must be "true" or "false" for boolean type.';
      }
    }

    this.setState(prevState => ({
      valueErrors: {
        ...prevState.valueErrors,
        [index]: errorMessage
      }
    }));
  };

  handleEnvNameChange = name => {
    this.setState(prev => ({
      environment: {
        name,
        ...prev.environment
      }
    }));
  };

  validate = () => {
    const { variables, selectedVariables, nameErrors, valueErrors } = this.state;
    const atLeastOneSelected = variables && Object.values(selectedVariables).some(value => value);
    const noNameErrors = Object.values(nameErrors).every(error => error === "");
    const noValueErrors = Object.values(valueErrors).every(error => error === "");
    return atLeastOneSelected && noNameErrors && noValueErrors;
  };

  saveVariables = () => {
    try {
      const { variables, selectedVariables, environmentName, isGlobal } = this.state;
      const selectedVars = variables.filter((_v, i) => selectedVariables[i]);

      const mappedVariables = selectedVars.map(variable => ({
        connectionId: null,
        dbColumns: [],
        logger: variable.description,
        mode: "static",
        name: variable.name,
        type: variable.type,
        value: variable.value,
        variableType: "API"
      }));

      const finalObject = {
        name: environmentName || "New Postman User Variable",
        isGlobal: isGlobal,
        variables: mappedVariables
      };

      this.props.save(finalObject);
    } catch (error) {
      toast.error("Error occurred while saving environment variables.");
      console.error("Something went wrong", error);
    }
  };

  getSelectedCount = () => {
    const { selectedVariables } = this.state;
    return Object.values(selectedVariables).filter(Boolean).length;
  };

  render() {
    const {
      variables,
      selectedVariables,
      selectedAll,
      nameErrors,
      valueErrors,
      environment,
      environmentName,
      isGlobal,
      fileName,
      configMode,
      configEnvironments,
      selectedEnvironments,
      configLoading
    } = this.state;

    const typeOptions = ["String", "Number", "Boolean"];

    return (
      <div className="postman-uploader-container">
        {/* Header */}
        <div className="postman-uploader-header">
          <div className="postman-uploader-header-icon">
            <i className="fas fa-cog"></i>
          </div>
          <div className="postman-uploader-header-text">
            <h3>{configMode ? "Import Configuration" : "Upload Postman Environment"}</h3>
            <p>
              {configMode
                ? "One variable set per environment. Credentials are encrypted and never shown again."
                : "Import a Postman environment, or any configuration file"}
            </p>
          </div>
        </div>

        {/* Body */}
        <div className="postman-uploader-body">
          {/* File Upload Zone */}
          <div className={`postman-file-upload-zone ${environment || configMode ? 'has-file' : ''}`}>
            {!environment && !configMode ? (
              <>
                <div className="postman-upload-icon">
                  <i className="fas fa-cloud-upload-alt"></i>
                </div>
                <h4>Drop your environment or configuration file here</h4>
                <p>A Postman environment, or a config such as program_configuration.json</p>
                <label className="postman-browse-btn">
                  <i className="fas fa-folder-open"></i>
                  Select File
                  <input
                    type="file"
                    accept=".json"
                    onChange={this.handleFileChange}
                    style={{ display: "none" }}
                  />
                </label>
              </>
            ) : (
              <div className="postman-file-selected">
                <div className="postman-file-selected-icon">
                  <i className="fas fa-check-circle"></i>
                </div>
                <div className="postman-file-selected-info">
                  <h5>{fileName}</h5>
                  <span>
                    {configMode
                      ? `Configuration read: ${configEnvironments.length} environment${
                          configEnvironments.length === 1 ? "" : "s"
                        }`
                      : "Environment loaded successfully"}
                  </span>
                </div>
                <label className="postman-browse-btn" style={{ marginLeft: 'auto' }}>
                  <i className="fas fa-sync-alt"></i>
                  Change File
                  <input
                    type="file"
                    accept=".json"
                    onChange={this.handleFileChange}
                    style={{ display: "none" }}
                  />
                </label>
              </div>
            )}
          </div>

          {configLoading && (
            <p style={{ textAlign: "center", margin: "12px 0" }}>
              <i className="fas fa-spinner fa-spin" /> Reading the file...
            </p>
          )}

          {/* Configuration file: one set per environment */}
          {configMode && (
            <div className="postman-table-section">
              <div className="postman-table-header-bar">
                <div className="postman-table-title">
                  {configEnvironments.length} environment
                  {configEnvironments.length === 1 ? "" : "s"} found
                </div>
                <label style={{ fontSize: 12 }}>
                  <input
                    type="checkbox"
                    checked={isGlobal}
                    onChange={e => this.setState({ isGlobal: e.target.checked })}
                  />{" "}
                  Global
                </label>
              </div>
              {configEnvironments.map(env => (
                <div key={env.name} style={{ marginBottom: 14 }}>
                  <label style={{ fontWeight: 600 }}>
                    <input
                      type="checkbox"
                      checked={!!selectedEnvironments[env.name]}
                      onChange={() => this.toggleEnvironment(env.name)}
                    />{" "}
                    {env.name}
                    <span style={{ fontWeight: 400, opacity: 0.7, marginLeft: 8 }}>
                      {env.variableCount} variables
                      {env.secretCount > 0 ? `, ${env.secretCount} encrypted` : ""}
                    </span>
                  </label>
                  <div className="postman-table-wrapper" style={{ maxHeight: 220 }}>
                    <table>
                      <thead>
                        <tr>
                          <th>Variable</th>
                          <th>Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {env.variables.map(v => (
                          <tr key={v.name}>
                            <td>
                              <code style={{ fontSize: 12 }}>{`{${v.name}}`}</code>
                            </td>
                            <td>
                              {v.secret ? (
                                <span
                                  title="Encrypted on import. The value stays only in your configuration file."
                                  style={{
                                    padding: "2px 8px",
                                    borderRadius: 4,
                                    fontSize: 10,
                                    fontWeight: 700,
                                    background: "rgba(245, 158, 11, 0.2)",
                                    color: "#fcd34d"
                                  }}
                                >
                                  ENCRYPTED
                                </span>
                              ) : (
                                <span style={{ fontSize: 12, opacity: 0.85 }}>{v.value}</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
              <p style={{ fontSize: 12, opacity: 0.7 }}>
                Enable a set on the variables screen to make it the active environment. Any
                header written as <code>{"{api_config.access_token}"}</code> then resolves from
                it.
              </p>
            </div>
          )}

          {/* Environment Configuration */}
          {environment && (
            <div className="postman-env-config">
              <div className="postman-env-config-field">
                <label>User Variable Name</label>
                <input
                  type="text"
                  value={environmentName}
                  onChange={e => this.setState({ environmentName: e.target.value })}
                  placeholder="Enter variable name..."
                />
              </div>
              <div className="postman-env-config-field postman-checkbox-field">
                <label>
                  <input
                    type="checkbox"
                    checked={isGlobal}
                    onChange={e => this.setState({ isGlobal: e.target.checked })}
                  />
                  Global Variable
                </label>
              </div>
            </div>
          )}

          {/* Variables Table */}
          {variables.length > 0 && (
            <div className="postman-table-section">
              <div className="postman-table-header-bar">
                <div className="postman-table-title">
                  <i className="fas fa-database"></i>
                  <span>Environment Variables</span>
                </div>
                <div className="postman-table-count">
                  {this.getSelectedCount()} of {variables.length} selected
                </div>
              </div>
              <div className="postman-table-wrapper">
                <table className="postman-table">
                  <thead>
                    <tr>
                      <th className="col-checkbox">
                        <input
                          type="checkbox"
                          className="postman-checkbox"
                          checked={selectedAll}
                          onChange={this.handleSelectAll}
                        />
                      </th>
                      <th className="col-name">Name</th>
                      <th className="col-value">Value</th>
                      <th className="col-type">Type</th>
                      <th className="col-description">Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {variables.map((variable, index) => (
                      <tr key={index}>
                        <td className="col-checkbox">
                          <input
                            type="checkbox"
                            className="postman-checkbox"
                            checked={selectedVariables[index]}
                            onChange={() => this.handleCheckboxChange(index)}
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            className={`postman-table-input ${nameErrors[index] ? "is-invalid" : ""}`}
                            value={variable.name}
                            onChange={e => this.handleVariableChange(index, "name", e.target.value)}
                          />
                          {nameErrors[index] && (
                            <div className="postman-error-message">{nameErrors[index]}</div>
                          )}
                        </td>
                        <td>
                          <input
                            type="text"
                            className={`postman-table-input ${valueErrors[index] ? "is-invalid" : ""}`}
                            value={variable.value}
                            onChange={e => this.handleVariableChange(index, "value", e.target.value)}
                          />
                          {valueErrors[index] && (
                            <div className="postman-error-message">{valueErrors[index]}</div>
                          )}
                        </td>
                        <td>
                          <select
                            className="postman-table-select"
                            value={variable.type}
                            onChange={e => this.handleVariableChange(index, "type", e.target.value)}
                          >
                            {typeOptions.map(typeOption => (
                              <option key={typeOption} value={typeOption}>
                                {typeOption}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <input
                            type="text"
                            className="postman-table-input"
                            value={variable.description}
                            onChange={e => this.handleVariableChange(index, "description", e.target.value)}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="postman-uploader-footer">
          <button onClick={this.props.close} type="button" className="postman-btn postman-btn-cancel">
            Cancel
          </button>
          {configMode ? (
            <button
              disabled={this.selectedEnvironmentNames().length === 0 || configLoading}
              onClick={this.importConfig}
              type="button"
              className="postman-btn postman-btn-primary"
            >
              <i className="fas fa-save"></i>
              Import Configuration
            </button>
          ) : (
            <button
              disabled={!this.validate()}
              onClick={this.saveVariables}
              type="submit"
              className="postman-btn postman-btn-primary"
            >
              <i className="fas fa-save"></i>
              Save Variables
            </button>
          )}
        </div>
      </div>
    );
  }
}

export default PostmanEnvironmentUploader;
